import { useEffect } from "react";
import { useRouter } from "next/router";
import type { PluginListenerHandle } from "@capacitor/core";
import { Sellwild, ensureSellwildConfigured, isSellwildNative } from "../utils/native/sellwild-plugin";

// Native ad bridge debug logging stays on regardless of NODE_ENV — too easy
// to lose visibility in TestFlight builds where the prod bundle has DEBUG
// off but ads are silently failing to fill.
const log = (...args: unknown[]) => console.log("[Sellwild]", ...args);

export function useSellwildNativeBootstrap() {
  const router = useRouter();

  useEffect(() => {
    if (!isSellwildNative()) {
      log("not native — skipping bootstrap");
      return;
    }
    log("configuring plugin…");
    // SDK 1.3.0+ resolves everything else (listingsUrl, ad zones, refresh,
    // colors, bidder configs) from the Sellwild CDN at
    // widget.sellwild.com/app/<partnerCode>/<slug>.json.
    //
    // partnerCode + slug are the only things partners hand us. Admin
    // dashboard for this app:
    // https://sellwild-widget.netlify.app/admin-v2/app/antengo-sellwild-tv
    void ensureSellwildConfigured({
      partnerCode: "antengo",
      slug: "antengo-sellwild-tv",
      // listingsUrl is optional in 1.3.0; the SDK derives it from the
      // remote config or partnerCode default. Kept here so the legacy
      // type signature still passes — the value is overridden by remote
      // config when present.
      listingsUrl: "https://api.sellwild.com/supplyListing/rpc",
      debug: true,
    })
      .then((r) => log("configured", r))
      .then(() => {
        // Request the device advertising id. On iOS this surfaces the ATT
        // prompt; on Android there is no prompt — the plugin fetches GAID
        // synchronously (or reports denied if the user has limit-ad-tracking
        // enabled in system settings). Either way the plugin writes the id
        // into window.__SELLWILD_APP__.idfa (see SellwildPlugin.swift:213
        // and SellwildPlugin.kt:152), and the web widget forwards it as
        // ortb2.device.ifa to GAM/Prebid.
        log("requesting tracking id…");
        return Sellwild.requestTrackingAuthorization();
      })
      .then((status) => log("tracking id", status))
      .catch((err: unknown) => {
        const msg = err instanceof Error ? err.message : String(err);
        console.warn("[Sellwild] bootstrap failed:", msg);
      });

    // Subscribe to ad lifecycle events. Without this, fill failures and
    // load errors are invisible from JS — the native plugin reports them
    // via these events, not via the showBanner promise.
    const handles: Promise<PluginListenerHandle>[] = [];
    handles.push(Sellwild.addListener("adImpression", (e) => log("adImpression", e)));
    handles.push(Sellwild.addListener("adClick", (e) => log("adClick", e)));
    handles.push(Sellwild.addListener("adError", (e) => log("adError", e)));

    return () => {
      void Promise.all(handles).then((hs) => hs.forEach((h) => void h.remove()));
    };
  }, []);

  // Mirror page views into the SDK's analytics stream on native.
  useEffect(() => {
    if (!isSellwildNative()) return;
    const handler = (url: string) => {
      log("trackEvent page_view", url);
      void Sellwild.trackEvent({ event: "page_view", action: url });
    };
    handler(router.asPath);
    router.events.on("routeChangeComplete", handler);
    return () => {
      router.events.off("routeChangeComplete", handler);
    };
  }, [router]);
}
